/**
 * 获取最高额度版本并更新state和localStorage
 */
import store, { updateModuleState } from '@/store';
import platform from '@/shared/utils/platform';
import { getGalileoAb } from '@/shared/galileoAb';
import { ABResEnum } from '@/enums/abExperiment';
import { computed, ComputedRef } from '@vue/composition-api';

// 额度配置常量
export const MAX_QUOTA_CONFIG = {
  CONTROL_GROUP: 200000, // 对照组：20万
  EXPERIMENT_GROUP: 300000, // 实验组：30万
} as const;

export const getAndStoreMaxQuota = async (): Promise<void> => {
  // 哈啰出行app&实验初始化状态，走实验
  if (platform.isEasyBike) {
    const { result } = await getGalileoAb().getAbResCatchError('202508141328078571', '202508141356373471') || {};
    if (result === ABResEnum.HINT) {
      // 更新state
      updateModuleState('base', {
        hitMaxQuotaExperiment: true,
      });
    }
  }
};

/**
 * 响应式获取最高额度数值
 * @returns 响应式的最高额度数值
 */
export const useMaxQuotaAmount = (): ComputedRef<number> => computed(() => {
  const hitExperiment = store.state.base.hitMaxQuotaExperiment;
  return hitExperiment ? MAX_QUOTA_CONFIG.EXPERIMENT_GROUP : MAX_QUOTA_CONFIG.CONTROL_GROUP;
});

/**
 * 响应式获取格式化的最高额度文案（带万单位）
 * @returns 响应式的格式化额度文案，如 "20万" 或 "30万"
 */
export const useMaxQuotaText = (): ComputedRef<string> => computed(() => {
  const amount = useMaxQuotaAmount().value;
  return `${amount / 10000}万`;
});

/**
 * 响应式获取格式化的最高额度文案（带逗号分隔）
 * @returns 响应式的格式化额度文案，如 "200,000" 或 "300,000"
 */
export const useMaxQuotaFormatted = (): ComputedRef<string> => computed(() => {
  const amount = useMaxQuotaAmount().value;
  return amount.toLocaleString();
});

/**
 * 直接获取最高额度文案值（非响应式）
 * @returns 当前的最高额度文案，如 "20万" 或 "30万"
 */
export const getMaxQuotaText = (): string => {
  const hitExperiment = store.state.base.hitMaxQuotaExperiment;
  const amount = hitExperiment ? MAX_QUOTA_CONFIG.EXPERIMENT_GROUP : MAX_QUOTA_CONFIG.CONTROL_GROUP;
  return `${amount / 10000}万`;
};

/**
 * 响应式获取更“真实”的最高额度（带逗号分隔）
 * @returns 响应式的实际额度文案，如 "528,000" 或 "63,000"
 */
export const useRealityMaxQuotaFormatted = (): ComputedRef<string> => computed(() => {
  let amount;
  const { creditInfo } = store.state.base.businessInfoExtra || {};

  if (creditInfo?.retentionPopupAmount) {
    amount = Number(creditInfo?.retentionPopupAmount);
  } else {
    amount = useMaxQuotaAmount().value;
  }

  return amount.toLocaleString();
});

/** 是否是真实额度实验组 */
export const useIsRealityQuotaExperimental = (): ComputedRef<boolean> => computed(() => {
  const { creditInfo } = store.state.base.businessInfoExtra || {};
  return !!creditInfo?.retentionPopupAmount;
});
